import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { UserRound } from 'lucide-react';
import { apiUrl } from '../config/api';
import { apiFetch } from '../utils/session';
import './AdminDashboard.css';

function AdminDashboard() {
  const [stats, setStats] = useState({
    pendingVerifications: 0,
    totalVerifiedMembers: 0,
    activeSosAlerts: 0,
    openHelpRequests: 0,
  });
  const [areas, setAreas] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [statsRes, areasRes] = await Promise.all([
          apiFetch(apiUrl('/admin/stats')),
          apiFetch(apiUrl('/admin/area-counts')),
        ]);
        const data = await statsRes.json();
        const areaData = await areasRes.json().catch(() => ({}));
        if (!statsRes.ok) throw new Error(data.error || 'Failed to load');
        if (!areasRes.ok) throw new Error(areaData.error || 'Failed to load area counts');
        if (!cancelled) {
          setStats(data);
          setAreas(areaData.areas || []);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not load dashboard');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="admin-page">
      <div className="admin-shell">
        <header className="admin-header">
          <div>
            <h1 className="admin-title">Admin dashboard</h1>
            <p className="admin-sub">Sangram Sena — operations overview</p>
          </div>
        </header>

        {error ? (
          <p className="admin-banner-error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="admin-stat-grid">
          <Link className="admin-stat-card admin-stat-card-accent" to="/admin/verify">
            <span className="admin-stat-label">Pending verifications</span>
            <span className="admin-stat-value">{stats.pendingVerifications}</span>
            <span className="admin-stat-hint">Review applications →</span>
          </Link>
          <Link className="admin-stat-card" to="/help/queue">
            <span className="admin-stat-label">Open help requests</span>
            <span className="admin-stat-value">{stats.openHelpRequests}</span>
            <span className="admin-stat-hint">Area help queue →</span>
          </Link>
          <Link className="admin-stat-card admin-stat-card-sos" to="/area-head/sos">
            <span className="admin-stat-label">Active SOS alerts</span>
            <span className="admin-stat-value">{stats.activeSosAlerts}</span>
            <span className="admin-stat-hint">Open emergency queue →</span>
          </Link>
          <Link className="admin-stat-card" to="/admin/members">
            <span className="admin-stat-label">Total verified members</span>
            <span className="admin-stat-value">{stats.totalVerifiedMembers}</span>
            <span className="admin-stat-hint">Manage all members →</span>
          </Link>
          <Link className="admin-stat-card" to="/business">
            <span className="admin-stat-label">Local business</span>
            <span className="admin-stat-value"><UserRound size={36} aria-hidden /></span>
            <span className="admin-stat-hint">Area services →</span>
          </Link>
          <Link className="admin-stat-card" to="/jobs">
            <span className="admin-stat-label">Jobs</span>
            <span className="admin-stat-value"><UserRound size={36} aria-hidden /></span>
            <span className="admin-stat-hint">Area work posts →</span>
          </Link>
          <Link className="admin-stat-card" to="/volunteer">
            <span className="admin-stat-label">Volunteer needs</span>
            <span className="admin-stat-value"><UserRound size={36} aria-hidden /></span>
            <span className="admin-stat-hint">Service posts →</span>
          </Link>
          <Link className="admin-stat-card" to="/community">
            <span className="admin-stat-label">My community</span>
            <span className="admin-stat-value"><UserRound size={36} aria-hidden /></span>
            <span className="admin-stat-hint">People in your area →</span>
          </Link>
          <Link className="admin-stat-card" to="/profile">
            <span className="admin-stat-label">My account</span>
            <span className="admin-stat-value"><UserRound size={36} aria-hidden /></span>
            <span className="admin-stat-hint">View my profile &amp; ID card →</span>
          </Link>
        </div>

        <h2 className="admin-title">Area counts</h2>
        <div className="admin-counts-wrap">
          <table className="admin-counts">
            <thead>
              <tr>
                <th>Area</th>
                <th>Open help</th>
                <th>Resolved this month</th>
                <th>Volunteer posts</th>
                <th>Open jobs</th>
              </tr>
            </thead>
            <tbody>
              {areas.map((row) => (
                <tr key={row.area}>
                  <td>{row.area}</td>
                  <td>{row.openHelpRequests}</td>
                  <td>{row.resolvedThisMonth}</td>
                  <td>{row.volunteerPosts}</td>
                  <td>{row.openJobs}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
